import { useMemo } from 'react'
import { collection, doc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { recordChange } from '@/lib/activity'
import { useSharedCollection } from '@/lib/sharedCollection'
import type { FieldDecision, FieldDecisionValue } from './types'
import { buildStatusOf, type BuildStatus } from './buildStatus'

/**
 * `enabled` mirrors `useDecisions`: the global search subscribes to every collection at
 * once, and a role without `fields.view` would get a permission error from the listener
 * rather than an empty list.
 */
export function useFieldDecisions(enabled = true) {
  const { rows, loading, error } = useSharedCollection<FieldDecision>(
    'fieldDecisions',
    (onRows, onError) => onSnapshot(
      collection(db, 'fieldDecisions'),
      (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FieldDecision)),
      onError,
    ),
    enabled,
  )
  return { fields: rows, loading, error }
}

export async function setFieldDecision(
  id: string,
  decision: FieldDecisionValue,
  actor: { email: string; name?: string | null },
  extra?: { ifChanging?: string; notes?: string; previous?: FieldDecision },
) {
  await updateDoc(doc(db, 'fieldDecisions', id), {
    decision,
    decided: decision !== '',
    ...(extra?.ifChanging !== undefined ? { ifChanging: extra.ifChanging } : {}),
    ...(extra?.notes !== undefined ? { notes: extra.notes } : {}),
    decidedBy: actor.email,
    decidedAt: serverTimestamp(),
  })

  // Every change, not only the verdict. Writing the log after the update means a rejected
  // write leaves no entry claiming something happened that did not.
  const prev = extra?.previous
  await recordChange(['fieldDecisions', id],
    { field: 'Decision', from: prev?.decision ?? '', to: decision || '(cleared)' }, actor)

  if (extra?.ifChanging !== undefined && extra.ifChanging !== (prev?.ifChanging ?? '')) {
    await recordChange(['fieldDecisions', id],
      { field: 'If changing', from: prev?.ifChanging ?? '', to: extra.ifChanging || '(cleared)' }, actor)
  }
  if (extra?.notes !== undefined && extra.notes !== (prev?.notes ?? '')) {
    await recordChange(['fieldDecisions', id],
      { field: 'Notes', from: prev?.notes ?? '', to: extra.notes || '(cleared)' }, actor)
  }
}

/**
 * Records where a migrating field's build stands, or the new-org field it was built as.
 *
 * Either half on its own: the status saves the moment it is picked and the location when it
 * is saved, and neither should wait on the other. Each is logged as its own change, so the
 * history says when a field was marked built and, separately, what it was built as.
 */
export async function setFieldBuild(
  field: FieldDecision,
  patch: { buildStatus?: BuildStatus | ''; builtAs?: string },
  actor: { email: string; name?: string | null },
) {
  const builtAs = patch.builtAs?.trim()
  await updateDoc(doc(db, 'fieldDecisions', field.id), {
    ...(patch.buildStatus !== undefined ? { buildStatus: patch.buildStatus } : {}),
    ...(builtAs !== undefined ? { builtAs } : {}),
    buildStatusBy: actor.email,
    buildStatusAt: serverTimestamp(),
  })

  if (patch.buildStatus !== undefined && patch.buildStatus !== buildStatusOf(field)) {
    await recordChange(['fieldDecisions', field.id],
      { field: 'Build status', from: buildStatusOf(field) || '(none)', to: patch.buildStatus || 'To Build' }, actor)
  }
  if (builtAs !== undefined && builtAs !== (field.builtAs ?? '')) {
    await recordChange(['fieldDecisions', field.id],
      { field: 'Built as', from: field.builtAs || '(blank)', to: builtAs || '(blank)' }, actor)
  }
}

export function useFieldFacets(fields: FieldDecision[]) {
  return useMemo(
    () => ({
      objects: [...new Set(fields.map((f) => f.object).filter(Boolean))].sort(),
      categories: [...new Set(fields.map((f) => f.category).filter(Boolean))].sort(),
      kinds: [...new Set(fields.map((f) => f.kind).filter(Boolean))].sort(),
    }),
    [fields],
  )
}
